/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { SubscriptionDto } from '@/commons/dtos';
import Banner from '@/components/banner/Banner';
import SubscriptionCard from '@/components/SubscriptionCard';
import { useAppDispatch } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { freApi } from '@/store/apis';
import { actions, fetchNavigationData, fetchSubNavigationData } from '@/store/Products';
import { Button, FlexBox, IllustratedMessage, Link, Text, Title } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Landing() {
    const { t } = useTranslations();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { data: subscriptions = [], isSuccess } = freApi.useGetSubscriptionsQuery();
    const { data: userInfo } = freApi.useGetUserInfoQuery();
    const [createProductRun] = freApi.useCreateProductRunMutation();

    const handleNavigation = async (sub: SubscriptionDto) => {
        dispatch(actions.setCurrentProduct({ productName: sub.productDto.name }));
        dispatch(actions.setCurrentSKU({ SKUId: sub.productDto.sku }));
        dispatch(actions.setCurrentRealm(sub.realm));

        try {
            dispatch(actions.setCurrentFlowId(sub.flowId));
            const flowId = sub.flowId;
            const phasesData = await dispatch(fetchNavigationData({ flowId: flowId }));
            const currentPhase = phasesData.payload[0];
            dispatch(
                actions.setCurrentPhase({
                    name: currentPhase.name,
                    id: currentPhase.id,
                    displayOrder: currentPhase.displayOrder,
                    translationNameKey: currentPhase.translationNameKey,
                })
            );
            const stepsData = await dispatch(fetchSubNavigationData({ phaseId: currentPhase.id }));
            dispatch(actions.setCurrentStep(stepsData.payload[0].id));
            if (sub.runId) {
                dispatch(actions.setCurrentRunId(sub.runId));
            } else {
                if (!userInfo) return;

                const createdProductRun = await createProductRun({
                    flowId,
                    sku: sub.productDto.sku,
                    userInfo: { crm_account_id: userInfo.crm_account_id, user_uuid: userInfo.user_uuid },
                }).unwrap();

                dispatch(actions.setCurrentRunId(createdProductRun.id));
                dispatch(actions.setCurrentRealm(createdProductRun.realm));
            }

            navigate('/dashboard');
        } catch (error) {
            console.error(error);
        }
    };

    const [singleProducts, suiteProducts] = useMemo(
        () =>
            subscriptions.reduce(
                (result: SubscriptionDto[][], sub) => {
                    const [singleProducts, suiteProducts] = result;
                    if (sub.productDto.skuType === 'SUITE') {
                        suiteProducts.push(sub);
                    } else {
                        singleProducts.push(sub);
                    }
                    return result;
                },
                [[], []]
            ),
        [subscriptions]
    );

    if (isSuccess && subscriptions.length === 0) {
        return (
            <FlexBox direction="Column">
                <Banner />
                <IllustratedMessage name="PageNotFound" titleText={t('EMPTY_SKU_SCREEN_TITLE')} subtitleText={t('EMPTY_SKU_SCREEN_SUBTITLE')} />
                <FlexBox justifyContent="Center">
                    <Link href="https://www.sap.com/products.html" target="_blank" rel="noopener noreferrer">
                        <Button design="Emphasized">{t('EXPLORE_SAP_PRODUCTS')}</Button>
                    </Link>
                </FlexBox>
            </FlexBox>
        );
    }

    return (
        <FlexBox direction="Column" style={{ width: '100%' }}>
            <Banner />
            {singleProducts.length > 0 && (
                <FlexBox style={{ padding: '3rem' }} gap={16} direction="Column">
                    <Title data-testid="single-product-scenario-title" level="H4" size="H4">
                        {t('WELCOME_SUBTITLE')} ({singleProducts.length})
                    </Title>
                    <Text data-testid="single-product-scenario-label" style={{ color: ThemingParameters.sapContent_LabelColor }}>
                        {t('SCENARIO_DESCRIPTION')}
                    </Text>

                    <FlexBox gap={16} wrap="Wrap">
                        {singleProducts.map(sub => (
                            <SubscriptionCard key={sub.productDto.sku} sub={sub} onNavigate={handleNavigation} />
                        ))}
                    </FlexBox>
                </FlexBox>
            )}
            {suiteProducts.length > 0 && (
                <FlexBox style={{ padding: '0 3rem 3rem 3rem' }} gap={16} direction="Column">
                    <Title data-testid="suite-scenario-title" level="H4" size="H4">
                        {t('SUITE_SCENARIO')} ({suiteProducts.length})
                    </Title>
                    <Text data-testid="suite-scenario-label" style={{ color: ThemingParameters.sapContent_LabelColor }}>
                        {t('SCENARIO_DESCRIPTION')}
                    </Text>

                    <FlexBox gap={16} wrap="Wrap">
                        {suiteProducts.map(sub => (
                            <SubscriptionCard key={sub.productDto.sku} sub={sub} onNavigate={handleNavigation} />
                        ))}
                    </FlexBox>
                </FlexBox>
            )}
        </FlexBox>
    );
}
